import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { search, type SearchEntry } from '@/lib/search';
import { Icon } from './Icon';

const SUGGESTIONS = ['阈值', '接入协议', '审计', 'RTK', '模拟机队', '私有化部署'];

/**
 * 全站搜索浮层。
 *
 * 纯前端：索引来自 `lib/search.ts`，输入即算，不发任何请求。
 * 键盘 ↑↓ 在结果间移动，Enter 打开，Esc 关闭。
 *
 * 用 portal 挂到 `document.body`，而不是就地渲染。原因是调用方把它放在 `<header>` 里，
 * 而顶栏带 `backdrop-blur` —— 带 backdrop-filter 的元素会成为 `position: fixed`
 * 后代的包含块，于是 `inset-0` 只铺满顶栏那条 64px 的带子，遮罩和模糊都只作用在一条上。
 * 挂到 body 同时也让这个 dialog 不再落在 banner 地标内部。
 */
export function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const listId = useId();

  const results = useMemo(() => search(query), [query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(0);
    const timer = window.setTimeout(() => inputRef.current?.focus(), 20);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  if (!open) return null;

  const go = (item: SearchEntry) => {
    onClose();
    navigate(item.href);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((value) => (value + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((value) => (value - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = results[cursor] ?? results[0];
      if (target) go(target);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="站内搜索">
      <button
        type="button"
        aria-label="关闭搜索"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-[rgb(21_27_33/0.45)] backdrop-blur-[2px]"
      />

      <div className="relative mx-auto mt-[12vh] w-full max-w-[42rem] px-4">
        <div className="overflow-hidden rounded-card border border-line bg-surface-1 shadow-lift">
          <div className="flex items-center gap-3 border-b border-line px-4 py-3">
            <Icon name="search" size={18} className="shrink-0 text-fg-subtle" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded={results.length > 0}
              aria-controls={listId}
              aria-autocomplete="list"
              placeholder="搜索产品、页面或工程动态"
              className="min-w-0 flex-1 bg-transparent text-[0.9375rem] text-fg outline-none placeholder:text-fg-subtle"
            />
            <kbd className="hidden shrink-0 rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem] text-fg-muted sm:block">
              Esc
            </kbd>
          </div>

          <div id={listId} role="listbox" className="max-h-[60vh] overflow-y-auto p-2">
            {query.trim() === '' ? (
              <div className="px-3 py-4">
                <p className="text-xs text-fg-muted">试试这些关键词</p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setQuery(item)}
                      className="badge-tint rounded-md px-2.5 py-1 text-xs font-medium transition-[filter] hover:brightness-105"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            ) : results.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-fg-muted">
                没有找到与「{query.trim()}」匹配的内容。
              </p>
            ) : (
              <ul>
                {results.map((item, index) => (
                  <li key={`${item.kind}-${item.id}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === cursor}
                      onMouseEnter={() => setCursor(index)}
                      onClick={() => go(item)}
                      className={[
                        'flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                        index === cursor ? 'bg-surface-2' : '',
                      ].join(' ')}
                    >
                      <span className="mt-0.5 shrink-0 text-fg-subtle">
                        <Icon
                          name={item.kind === '产品' ? 'layers' : item.kind === '动态' ? 'audit' : 'info'}
                          size={16}
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-fg">{item.title}</span>
                          <span className="shrink-0 text-[0.6875rem] text-fg-subtle">{item.kind}</span>
                        </span>
                        <span className="mt-0.5 line-clamp-1 block text-xs text-fg-muted">{item.description}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
